import axios from 'axios';

import { auditApi } from '@/lib/admin/admin-l4-api';
import { api, unwrap, type ApiEnvelope } from '@/lib/api';
import { financeApi } from '@/lib/finance/finance-api';
import { ordersApi } from '@/lib/orders/orders-api';
import { PAYMENT_METHOD_LABEL } from '@/lib/orders/labels';
import type { Order, OrderStatus, PaymentMethod } from '@/lib/orders/types';
import { supportApi } from '@/lib/support/support-api';

type IssueCode = 'unavailable' | 'forbidden' | 'error';

export interface ReportingIssue {
  module: 'orders' | 'finance' | 'support' | 'audit' | 'reporting';
  code: IssueCode;
  message: string;
}

export interface HomeRevenuePoint {
  date: string;
  revenue: number;
  orders: number;
}

export interface HomePaymentMixItem {
  method: string;
  label: string;
  count: number;
  amount?: number;
  color?: string;
}

export interface HomeOrderSummary {
  id: string;
  number: string;
  customerLabel: string;
  city: string;
  totalAmount: number;
  status: string;
  paymentStatus: string;
  paymentMethod: string | null;
  createdAt: string;
  lineCount?: number;
  hubLabel?: string | null;
}

export interface HomeActivityItem {
  id: string;
  at: string;
  actorLabel: string;
  action: string;
  category: string;
  target: string | null;
  result: 'success' | 'failure';
}

export interface HomeFinanceSummary {
  treasury: number;
  receivables: number;
  payables: number;
  overdueCount: number;
  overdueAmount: number;
  alerts: Array<{ level: 'info' | 'warn' | 'danger'; message: string }>;
}

export interface HomeSupportSummary {
  total: number;
  open: number;
  pending: number;
  urgent: number;
  resolved: number;
  unassigned: number;
  slaBreached: number;
}

export interface HomeOrdersSummary {
  todayCount: number;
  todayRevenue: number;
  averageBasket: number;
  inProgress: number;
  slaLate: number;
  byStatus: Record<string, number>;
  generatedAt?: string;
}

export interface HomeReport {
  generatedAt: string;
  source: 'endpoint' | 'adapter' | 'loading';
  issues: ReportingIssue[];
  orders: HomeOrdersSummary;
  latestOrders: HomeOrderSummary[];
  activity: HomeActivityItem[];
  revenueSeries: HomeRevenuePoint[];
  paymentMix: HomePaymentMixItem[];
  finance: HomeFinanceSummary | null;
  support: HomeSupportSummary | null;
}

const EMPTY_ORDERS: HomeOrdersSummary = {
  todayCount: 0,
  todayRevenue: 0,
  averageBasket: 0,
  inProgress: 0,
  slaLate: 0,
  byStatus: {},
};

const IN_PROGRESS_STATUSES: OrderStatus[] = [
  'PENDING_PAYMENT',
  'PAID',
  'CONFIRMED',
  'PROCURING',
  'IN_HUB',
  'READY_TO_SHIP',
  'SHIPPING',
  'DISPUTED',
];

const PAYMENT_COLORS: Record<string, string> = {
  MOMO: '#C6563A',
  OM: '#1E4D3B',
  CARD: '#D4A24C',
  COD: '#8A8074',
  BANK_TRANSFER: '#3A6A8C',
};

const isFallbackStatus = (status?: number): boolean =>
  status === 403 || status === 404 || status === 405 || status === 501;

const issueFor = (module: ReportingIssue['module'], error: unknown): ReportingIssue => {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    if (status === 403) {
      return { module, code: 'forbidden', message: 'Permission manquante.' };
    }
    if (isFallbackStatus(status)) {
      return { module, code: 'unavailable', message: 'Donnée indisponible.' };
    }
  }
  return { module, code: 'error', message: 'Chargement impossible.' };
};

async function optional<T>(
  module: ReportingIssue['module'],
  issues: ReportingIssue[],
  loader: () => Promise<T>,
): Promise<T | null> {
  try {
    return await loader();
  } catch (error) {
    issues.push(issueFor(module, error));
    return null;
  }
}

function customerLabel(order: Order): string {
  const c = order.customer;
  if (!c) return 'Client';
  if (c.displayName) return c.displayName;
  const name = [c.firstName, c.lastName].filter(Boolean).join(' ').trim();
  return name || c.email || c.phone || 'Client';
}

function mapOrder(order: Order): HomeOrderSummary {
  return {
    id: order.id,
    number: order.number,
    customerLabel: customerLabel(order),
    city: order.shippingAddress?.city ?? order.billingAddress?.city ?? '',
    totalAmount: order.totalAmount,
    status: order.status,
    paymentStatus: order.paymentStatus,
    paymentMethod: order.paymentMethod,
    createdAt: order.createdAt,
    lineCount: order.lineCount ?? order.lines?.length,
    hubLabel: order.hub?.code ?? order.hub?.name ?? null,
  };
}

function buildPaymentMix(orders: Order[]): HomePaymentMixItem[] {
  const byMethod = new Map<string, { count: number; amount: number }>();
  for (const order of orders) {
    const method = order.paymentMethod;
    if (!method) continue;
    const current = byMethod.get(method) ?? { count: 0, amount: 0 };
    current.count += 1;
    current.amount += order.totalAmount;
    byMethod.set(method, current);
  }
  return [...byMethod.entries()].map(([method, stats]) => ({
    method,
    label: PAYMENT_METHOD_LABEL[method as PaymentMethod] ?? method,
    count: stats.count,
    amount: stats.amount,
    color: PAYMENT_COLORS[method],
  }));
}

function normalizeEndpointReport(report: Partial<HomeReport>): HomeReport {
  return {
    generatedAt: report.generatedAt ?? new Date().toISOString(),
    source: 'endpoint',
    issues: report.issues ?? [],
    orders: { ...EMPTY_ORDERS, ...report.orders },
    latestOrders: report.latestOrders ?? [],
    activity: report.activity ?? [],
    revenueSeries: report.revenueSeries ?? [],
    paymentMix: report.paymentMix ?? [],
    finance: report.finance ?? null,
    support: report.support ?? null,
  };
}

async function loadEndpointHome(days?: number): Promise<HomeReport | null> {
  try {
    const report = await unwrap<HomeReport>(
      api.get<ApiEnvelope<HomeReport>>('/admin/reporting/home', {
        params: days ? { days } : undefined,
      }),
    );
    return normalizeEndpointReport(report);
  } catch (error) {
    if (axios.isAxiosError(error) && isFallbackStatus(error.response?.status)) {
      return null;
    }
    throw error;
  }
}

async function composeHomeReport(): Promise<HomeReport> {
  const issues: ReportingIssue[] = [];
  const [realtime, orderList, finance, support, audit] = await Promise.all([
    optional('orders', issues, () => ordersApi.realtimeStats()),
    optional('orders', issues, () =>
      ordersApi.list({ take: 12, skip: 0, sortBy: 'createdAt', sortDir: 'DESC' }),
    ),
    optional('finance', issues, () => financeApi.dashboard()),
    optional('support', issues, () => supportApi.stats()),
    optional('audit', issues, () => auditApi.list({ page: 1, pageSize: 12 })),
  ]);

  const byStatus = realtime?.byStatus ?? {};
  const todayCount = realtime?.todayCount ?? 0;
  const todayRevenue = realtime?.todayRevenue ?? 0;
  const latestOrderItems = orderList?.items ?? [];

  return {
    generatedAt: new Date().toISOString(),
    source: 'adapter',
    issues,
    orders: {
      todayCount,
      todayRevenue,
      averageBasket: todayCount > 0 ? Math.round(todayRevenue / todayCount) : 0,
      inProgress: IN_PROGRESS_STATUSES.reduce((sum, status) => sum + (byStatus[status] ?? 0), 0),
      slaLate: realtime?.slaLate ?? 0,
      byStatus,
      generatedAt: realtime?.generatedAt,
    },
    latestOrders: latestOrderItems.map(mapOrder),
    activity:
      audit?.items.map((entry) => ({
        id: entry.id,
        at: entry.at,
        actorLabel: entry.actorLabel,
        action: entry.action,
        category: entry.category,
        target: entry.target,
        result: entry.result,
      })) ?? [],
    revenueSeries: [],
    paymentMix: buildPaymentMix(latestOrderItems),
    finance: finance
      ? {
          treasury: finance.treasury,
          receivables: finance.receivables,
          payables: finance.payables,
          overdueCount: finance.overdueCount,
          overdueAmount: finance.overdueAmount,
          alerts: finance.alerts ?? [],
        }
      : null,
    support: support
      ? {
          total: support.total,
          open: support.open,
          pending: support.pending,
          urgent: support.urgent,
          resolved: support.resolved,
          unassigned: support.unassigned,
          slaBreached: support.slaBreached,
        }
      : null,
  };
}

export const reportingHomeApi = {
  home: async (days?: number): Promise<HomeReport> =>
    (await loadEndpointHome(days)) ?? composeHomeReport(),
};
